/**
 * FactSet value-scale semantics and Excel number-format helpers.
 *
 * FactSet return items commonly return percentage points: 75 means 75%, not
 * 7,500%. Excel's native percent token multiplies the displayed value by 100,
 * so known percentage-point values must use a literal percent sign.
 */

import { extractFqlArgumentExpression } from "./fqlDatePolicy";

export type FactSetValueSemantics =
  | "percentage_points"
  | "decimal_fraction"
  | "number"
  | "unknown";

export type FactSetFormatHintConfidence = "high" | "medium" | "low";

export type FactSetFormatHintSource =
  | "metadata"
  | "data_item"
  | "unknown";

export interface FactSetFormatHint {
  semantics: FactSetValueSemantics;
  confidence: FactSetFormatHintConfidence;
  source: FactSetFormatHintSource;
  dataItem?: string;
  recommendedNumberFormat?: string;
}

export interface InferFactSetValueSemanticsInput {
  formula: string;
  formulaTemplate?: string;
  formulaDetails?: Record<string, unknown>;
}

export const FACTSET_PERCENT_POINT_NUMBER_FORMAT = '0.00"%"';
export const EXCEL_DECIMAL_PERCENT_NUMBER_FORMAT = "0.00%";

/**
 * Audited FactSet items whose direct output is expressed in percentage
 * points. Keep this list exact: magnitude, labels, and broad "return" wording
 * are not reliable storage-scale evidence.
 */
const KNOWN_PERCENT_POINT_ITEMS = new Set([
  "P_TOTAL_RETURN",
  "P_PRICE_RETURNS",
]);

const IGNORED_FQL_TOKENS = new Set([
  "ANN_R",
  "COMPANY_SHORT",
  "DATEJ",
  "LTM_R",
  "QTR_R",
  "SEMI_R",
]);

const METADATA_UNIT_KEYS = new Set([
  "displayunit",
  "outputscale",
  "scale",
  "unit",
  "unitofmeasure",
  "units",
  "valuescale",
]);

/** True for direct FactSet worksheet formulas, including imported `_xll.` forms. */
export function isDirectFactSetFormula(formula: unknown): formula is string {
  return (
    typeof formula === "string" &&
    /^\s*=\s*(?:_XLL\.)?(?:FDS|FDSB|FDSC|FDSRC|FDSZ|FDT)\s*\(/i.test(
      formula,
    )
  );
}

/**
 * Extract likely FQL data-item mnemonics from the actual FQL argument.
 *
 * Looking only inside the final FDS-family argument prevents identifiers,
 * worksheet references, and wrapper names from being mistaken for data items.
 */
export function extractFactSetDataItems(formula: string): string[] {
  if (!isDirectFactSetFormula(formula)) return [];
  const fqlExpression = extractFqlArgumentExpression(
    stripImportedXllPrefix(formula),
  );
  if (!fqlExpression) return [];

  const withoutPlaceholders = fqlExpression.replace(/\{[^}]*\}/g, " ");
  const tokens =
    withoutPlaceholders.toUpperCase().match(/\b[A-Z][A-Z0-9_]{2,}\b/g) ??
    [];
  const unique = new Set<string>();
  for (const token of tokens) {
    if (!token.includes("_") || IGNORED_FQL_TOKENS.has(token)) continue;
    unique.add(token);
  }
  return Array.from(unique);
}

/**
 * True when a formula returns one atomic provider item without Excel-side or
 * FQL-side arithmetic. This intentionally rejects `/100`, compound FQL, and
 * formulas whose FQL is supplied indirectly by another cell.
 */
export function isAtomicFactSetDataItemFormula(
  formula: string,
  dataItem: string,
): boolean {
  if (!isDirectFactSetFormula(formula)) return false;
  const normalizedFormula = stripImportedXllPrefix(formula);
  const wrapperMatch =
    /^\s*=\s*(?:FDS|FDSB|FDSC|FDSRC|FDSZ|FDT)\s*\(/i.exec(
      normalizedFormula,
    );
  if (!wrapperMatch) return false;

  const wrapperOpen = normalizedFormula.indexOf("(", wrapperMatch.index);
  const wrapperClose = findMatchingExcelParenthesis(
    normalizedFormula,
    wrapperOpen,
  );
  if (
    wrapperClose < 0 ||
    normalizedFormula.slice(wrapperClose + 1).trim().length > 0
  ) {
    return false;
  }

  const items = extractFactSetDataItems(normalizedFormula);
  if (
    items.length !== 1 ||
    items[0].toUpperCase() !== dataItem.toUpperCase()
  ) {
    return false;
  }

  const fqlExpression = extractFqlArgumentExpression(normalizedFormula);
  const fqlShape = fqlExpression
    ? reconstructFqlShape(fqlExpression).trim()
    : "";
  const itemMatch = /^([A-Z][A-Z0-9_]*)\s*\(/i.exec(fqlShape);
  if (
    !itemMatch ||
    itemMatch[1].toUpperCase() !== dataItem.toUpperCase()
  ) {
    return false;
  }
  const itemOpen = fqlShape.indexOf("(", itemMatch.index);
  const itemClose = findMatchingPlainParenthesis(fqlShape, itemOpen);
  return itemClose >= 0 && fqlShape.slice(itemClose + 1).trim().length === 0;
}

/**
 * Infer output scale without inspecting the returned numeric value.
 *
 * Explicit TTF output metadata is authoritative for untransformed direct
 * formulas. Otherwise only exact audited data-item rules can select
 * percentage-point formatting.
 */
export function inferFactSetFormatHint(
  input: InferFactSetValueSemanticsInput,
): FactSetFormatHint {
  const formulas = [input.formula, input.formulaTemplate].filter(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );
  const dataItems = Array.from(
    new Set(formulas.flatMap((formula) => extractFactSetDataItems(formula))),
  );
  const dataItem = dataItems.length === 1 ? dataItems[0] : undefined;
  const isUnambiguous =
    dataItem !== undefined &&
    dataItems.length === 1 &&
    formulas.every((formula) => {
      if (!isDirectFactSetFormula(formula)) return false;
      return isAtomicFactSetDataItemFormula(formula, dataItem);
    });

  if (!isUnambiguous) {
    return createHint("unknown", "low", "unknown");
  }

  const metadataSemantics = inferFromMetadata(input.formulaDetails);
  if (metadataSemantics) {
    return createHint(metadataSemantics, "high", "metadata", dataItem);
  }

  if (dataItem && KNOWN_PERCENT_POINT_ITEMS.has(dataItem)) {
    return createHint(
      "percentage_points",
      "high",
      "data_item",
      dataItem,
    );
  }

  return createHint("unknown", "low", "unknown", dataItem);
}

/**
 * Detect an unquoted, unescaped Excel percent token. Such a token multiplies
 * the displayed value by 100.
 */
export function hasMultiplyingPercentToken(format: string): boolean {
  return transformPercentTokens(format, "detect").changed;
}

/** True when a percent sign is displayed literally without scaling the value. */
export function hasLiteralPercentToken(format: string): boolean {
  let inQuotes = false;
  let inBrackets = false;
  for (let index = 0; index < format.length; index += 1) {
    const char = format[index];
    if (char === "\\") {
      if (format[index + 1] === "%") return true;
      index += 1;
      continue;
    }
    if (char === "_") {
      index += 1;
      continue;
    }
    if (char === "*") {
      if (format[index + 1] === "%") return true;
      index += 1;
      continue;
    }
    if (char === '"' && !inBrackets) {
      if (inQuotes && format[index + 1] === '"') {
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && char === "[") {
      inBrackets = true;
      continue;
    }
    if (!inQuotes && char === "]") {
      inBrackets = false;
      continue;
    }
    if (char === "%" && inQuotes && !inBrackets) return true;
  }
  return false;
}

/** Convert multiplying percent tokens into quoted literal percent signs. */
export function literalizePercentTokens(format: string): string {
  return transformPercentTokens(format, "literalize").format;
}

/**
 * Add a literal percent sign to a numeric format that displays none.
 *
 * A percentage-point value read as a bare number is indistinguishable from an
 * ordinary quantity, so the sign is inserted after the trailing digit
 * placeholders of every numeric section — after the placeholders rather than at
 * the end of the section so suffixes such as `_)` and `)` keep their position.
 * `General`, text-only, and date sections are returned untouched.
 */
export function ensureLiteralPercentToken(format: string): string {
  const sections = splitNumberFormatSections(format);
  const updated = sections.map(appendLiteralPercentToSection);
  return updated.some((section, index) => section !== sections[index])
    ? updated.join(";")
    : format;
}

/**
 * Normalize a requested number format for known FactSet semantics.
 *
 * Unknown and ordinary numeric formats are deliberately preserved. A known
 * percentage-point value literalizes active percent tokens and gains a literal
 * percent sign when it displays none; a metadata-backed decimal fraction
 * rejects a literal-only percent format because that would display the value
 * 100x too small.
 */
export function normalizeFactSetNumberFormat(
  format: string,
  semantics: FactSetValueSemantics,
): { format: string; changed: boolean } {
  if (semantics === "percentage_points") {
    if (hasMultiplyingPercentToken(format)) {
      const normalized = literalizePercentTokens(format);
      return { format: normalized, changed: normalized !== format };
    }
    if (hasLiteralPercentToken(format)) {
      return { format, changed: false };
    }
    const withPercent = ensureLiteralPercentToken(format);
    return { format: withPercent, changed: withPercent !== format };
  }
  if (
    semantics === "decimal_fraction" &&
    hasLiteralPercentToken(format)
  ) {
    return {
      format: EXCEL_DECIMAL_PERCENT_NUMBER_FORMAT,
      changed: format !== EXCEL_DECIMAL_PERCENT_NUMBER_FORMAT,
    };
  }
  return { format, changed: false };
}

function createHint(
  semantics: FactSetValueSemantics,
  confidence: FactSetFormatHintConfidence,
  source: FactSetFormatHintSource,
  dataItem?: string,
): FactSetFormatHint {
  const recommendedNumberFormat =
    semantics === "percentage_points"
      ? FACTSET_PERCENT_POINT_NUMBER_FORMAT
      : semantics === "decimal_fraction"
        ? EXCEL_DECIMAL_PERCENT_NUMBER_FORMAT
        : undefined;
  return {
    semantics,
    confidence,
    source,
    ...(dataItem ? { dataItem } : {}),
    ...(recommendedNumberFormat ? { recommendedNumberFormat } : {}),
  };
}

function inferFromMetadata(
  details: Record<string, unknown> | undefined,
): FactSetValueSemantics | undefined {
  if (!details) return undefined;
  const values: string[] = [];
  collectMetadataUnitValues(details, values, 0);

  for (const value of values) {
    if (
      /\b(?:decimal fraction|decimal percent|fractional percent|percent as decimal|0\s*(?:to|-)\s*1)\b/.test(
        value,
      )
    ) {
      return "decimal_fraction";
    }
  }
  for (const value of values) {
    if (/\bbasis points?\b/.test(value)) continue;
    if (
      value === "%" ||
      /\b(?:percent|percentage|percentage point|percentage points|pct)\b/.test(
        value,
      )
    ) {
      return "percentage_points";
    }
  }
  return undefined;
}

function collectMetadataUnitValues(
  value: Record<string, unknown>,
  output: string[],
  depth: number,
): void {
  if (depth > 2) return;
  for (const [rawKey, rawValue] of Object.entries(value)) {
    const key = rawKey.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (
      METADATA_UNIT_KEYS.has(key) &&
      (typeof rawValue === "string" || typeof rawValue === "number")
    ) {
      output.push(String(rawValue).trim().toLowerCase());
      continue;
    }
    if (
      rawValue !== null &&
      typeof rawValue === "object" &&
      !Array.isArray(rawValue)
    ) {
      collectMetadataUnitValues(
        rawValue as Record<string, unknown>,
        output,
        depth + 1,
      );
    }
  }
}

type PercentTokenTransformMode = "detect" | "literalize";

function transformPercentTokens(
  input: string,
  mode: PercentTokenTransformMode,
): { format: string; changed: boolean } {
  let output = "";
  let inQuotes = false;
  let inBrackets = false;
  let changed = false;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (char === "\\" || char === "_" || char === "*") {
      output += char;
      if (index + 1 < input.length) {
        output += input[index + 1];
        index += 1;
      }
      continue;
    }
    if (char === '"' && !inBrackets) {
      output += char;
      if (inQuotes && input[index + 1] === '"') {
        output += input[index + 1];
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && char === "[") {
      inBrackets = true;
      output += char;
      continue;
    }
    if (!inQuotes && char === "]") {
      inBrackets = false;
      output += char;
      continue;
    }
    if (char !== "%" || inQuotes || inBrackets) {
      output += char;
      continue;
    }

    changed = true;
    if (mode === "detect") return { format: input, changed: true };
    let runLength = 1;
    while (input[index + runLength] === "%") runLength += 1;
    output += `"${"%".repeat(runLength)}"`;
    index += runLength - 1;
  }

  return { format: output, changed };
}

/** Split a number format on its top-level section separators. */
function splitNumberFormatSections(format: string): string[] {
  const sections: string[] = [];
  let current = "";
  let inQuotes = false;
  let inBrackets = false;

  for (let index = 0; index < format.length; index += 1) {
    const char = format[index];
    if (char === "\\" || char === "_" || char === "*") {
      current += char;
      if (index + 1 < format.length) {
        current += format[index + 1];
        index += 1;
      }
      continue;
    }
    if (char === '"' && !inBrackets) {
      current += char;
      if (inQuotes && format[index + 1] === '"') {
        current += format[index + 1];
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && char === "[") {
      inBrackets = true;
      current += char;
      continue;
    }
    if (!inQuotes && char === "]") {
      inBrackets = false;
      current += char;
      continue;
    }
    if (char === ";" && !inQuotes && !inBrackets) {
      sections.push(current);
      current = "";
      continue;
    }
    current += char;
  }

  sections.push(current);
  return sections;
}

function appendLiteralPercentToSection(section: string): string {
  if (hasMultiplyingPercentToken(section) || hasLiteralPercentToken(section)) {
    return section;
  }

  let inQuotes = false;
  let inBrackets = false;
  let placeholderEnd = -1;
  let sawDateToken = false;

  for (let index = 0; index < section.length; index += 1) {
    const char = section[index];
    if (char === "\\" || char === "_" || char === "*") {
      index += 1;
      continue;
    }
    if (char === '"' && !inBrackets) {
      if (inQuotes && section[index + 1] === '"') {
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && char === "[") {
      inBrackets = true;
      continue;
    }
    if (!inQuotes && char === "]") {
      inBrackets = false;
      continue;
    }
    if (inQuotes || inBrackets) continue;
    if (char === "d" || char === "D" || char === "y" || char === "Y") {
      sawDateToken = true;
    }
    if (char === "0" || char === "#" || char === "?") {
      placeholderEnd = index + 1;
    }
  }

  if (sawDateToken || placeholderEnd < 0) return section;
  return `${section.slice(0, placeholderEnd)}"%"${section.slice(placeholderEnd)}`;
}

function stripImportedXllPrefix(formula: string): string {
  return formula.replace(/^(\s*=\s*)_XLL\./i, "$1");
}

function reconstructFqlShape(expression: string): string {
  const segments = splitTopLevelConcatenation(expression);
  return segments
    .map((segment) => decodeWholeExcelString(segment.trim()) ?? "{value}")
    .join("");
}

function splitTopLevelConcatenation(expression: string): string[] {
  const segments: string[] = [];
  let current = "";
  let depth = 0;
  let inString = false;
  for (let index = 0; index < expression.length; index += 1) {
    const char = expression[index];
    if (char === '"') {
      current += char;
      if (inString && expression[index + 1] === '"') {
        current += expression[index + 1];
        index += 1;
      } else {
        inString = !inString;
      }
      continue;
    }
    if (!inString && char === "(") depth += 1;
    if (!inString && char === ")") depth = Math.max(0, depth - 1);
    if (!inString && depth === 0 && char === "&") {
      segments.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  segments.push(current);
  return segments;
}

function decodeWholeExcelString(segment: string): string | undefined {
  if (
    segment.length < 2 ||
    segment[0] !== '"' ||
    segment[segment.length - 1] !== '"'
  ) {
    return undefined;
  }
  let output = "";
  for (let index = 1; index < segment.length - 1; index += 1) {
    if (segment[index] === '"' && segment[index + 1] === '"') {
      output += '"';
      index += 1;
      continue;
    }
    if (segment[index] === '"') return undefined;
    output += segment[index];
  }
  return output;
}

function findMatchingExcelParenthesis(
  input: string,
  openIndex: number,
): number {
  if (openIndex < 0 || input[openIndex] !== "(") return -1;
  let depth = 0;
  let inString = false;
  for (let index = openIndex; index < input.length; index += 1) {
    const char = input[index];
    if (char === '"') {
      if (inString && input[index + 1] === '"') {
        index += 1;
      } else {
        inString = !inString;
      }
      continue;
    }
    if (inString) continue;
    if (char === "(") depth += 1;
    if (char === ")") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
}

function findMatchingPlainParenthesis(
  input: string,
  openIndex: number,
): number {
  if (openIndex < 0 || input[openIndex] !== "(") return -1;
  let depth = 0;
  for (let index = openIndex; index < input.length; index += 1) {
    if (input[index] === "(") depth += 1;
    if (input[index] === ")") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
}
